import { Component, Input, OnInit, signal } from '@angular/core';
import { UntypedFormControl, Validators } from '@angular/forms';
import { SelectItem } from 'primeng/api';
import { CustomEPP, ExternalPressureProfile, ExternalPressureProfileType, FluidToShoeEPP, InterpolationToPorePressureEPP, MudAndWetCementEPP, MudAndWetCementMixWaterEPP, MudAndPorePressureEPP, PericalLinkEPP, PorePressureEPP, PressurePoint } from '../models/external-pressure-profile.model';
import { CementingLanding } from 'src/app/wellbore-inputs/models/cementing-landing.model';
import { createForm, FormType, subformComponentProviders } from 'ngx-sub-form';
import { PoreFrac } from 'src/app/perivis/models/poreFrac.model';
import { BaseOperation } from 'src/app/perical/models/thermal-operation.model';
import { UserUnitsModel } from 'src/app/core/components/user-units/user-units.model';
import { LoadCaseService } from 'src/app/perivis/services/load-case.service';
import { WellComponent } from 'src/app/wellbore-inputs/components/well-configuration/models/well-component';

export interface ExternalPressureProfileForm {
  type: ExternalPressureProfileType;
  customEpp: CustomEPP;
  pericalLinkEpp: PericalLinkEPP;
  fluidToShoeEpp: FluidToShoeEPP;
  mudAndWetCementEpp: MudAndWetCementEPP;
  mudAndCementMixWaterEpp: MudAndWetCementMixWaterEPP;
  interpolationToPorePressureEpp: InterpolationToPorePressureEPP;
  mudAndPorePressureEpp: MudAndPorePressureEPP;
  porePressureEpp: PorePressureEPP;
  trappedCasingPressureEpp: any;
}

@Component({
  selector: 'app-external-pressure-profiles',
  templateUrl: './external-pressure-profiles.component.html',
  styles: [`
    ::ng-deep .p-panel-content-wrapper {
      width: 100%;
      max-width: 100%;
      min-width: 0;
    }
`],
  providers: subformComponentProviders(ExternalPressureProfilesComponent),
  standalone: false
})
export class ExternalPressureProfilesComponent implements OnInit {

  @Input() shoeDepth: number;
  @Input() currentString: any;
  @Input() cement: CementingLanding;
  @Input() porePressures: PoreFrac[];
  @Input() fracPressures: PoreFrac[];
  @Input() tubulars: WellComponent[];
  @Input() thermalOperations: Partial<BaseOperation>[];
  @Input() userUnits: UserUnitsModel;
  @Input() wellTd: number;
  @Input() isReadOnly: boolean;
  @Input() loadCaseIndex: number;

  constructor(private _loadCaseService: LoadCaseService) { }

  public form = createForm<ExternalPressureProfile, ExternalPressureProfileForm>(this, {
    formType: FormType.SUB,
    formControls: {
      type: new UntypedFormControl(null, [Validators.required]),
      customEpp: new UntypedFormControl(null),
      pericalLinkEpp: new UntypedFormControl(null),
      fluidToShoeEpp: new UntypedFormControl(null),
      mudAndWetCementEpp: new UntypedFormControl(null),
      mudAndCementMixWaterEpp: new UntypedFormControl(null),
      interpolationToPorePressureEpp: new UntypedFormControl(null),
      mudAndPorePressureEpp: new UntypedFormControl(null),
      porePressureEpp: new UntypedFormControl(null),
      trappedCasingPressureEpp: new UntypedFormControl(null)
    },
    toFormGroup: (obj: ExternalPressureProfile | null): ExternalPressureProfileForm | null => {
      if (!obj) {
        return null;
      }

      return {
        type: obj.type,
        customEpp: obj.type === ExternalPressureProfileType.CUSTOMEPP ? obj : null,
        pericalLinkEpp: obj.type === ExternalPressureProfileType.PERICALLINK ? obj : null,
        fluidToShoeEpp: obj.type === ExternalPressureProfileType.FLUIDTOSHOE ? obj : null,
        mudAndWetCementEpp: obj.type === ExternalPressureProfileType.MUDANDWETCEMENT ? obj : null,
        mudAndCementMixWaterEpp: obj.type === ExternalPressureProfileType.MUDANDCEMENTMIXWATER ? obj : null,
        interpolationToPorePressureEpp: obj.type === ExternalPressureProfileType.INTERPOLATIONTOPOREPRSSURE ? obj : null,
        mudAndPorePressureEpp: obj.type === ExternalPressureProfileType.MUDANDPOREPRESSURE ? obj : null,
        porePressureEpp: obj.type === ExternalPressureProfileType.POREPRESSURE ? obj : null,
        trappedCasingPressureEpp: obj.type === ExternalPressureProfileType.TRAPPEDCASINGPRESSURE ? obj : null
      };
    },
    fromFormGroup: (formValue: ExternalPressureProfileForm): ExternalPressureProfile | null => {
      const { type, customEpp, pericalLinkEpp, fluidToShoeEpp, mudAndWetCementEpp, mudAndCementMixWaterEpp, interpolationToPorePressureEpp, mudAndPorePressureEpp, porePressureEpp, trappedCasingPressureEpp, ...commonValues } = formValue;

      switch (type) {
        case ExternalPressureProfileType.CUSTOMEPP:
          return customEpp ? { type, ...customEpp, ...commonValues } : null;
        case ExternalPressureProfileType.PERICALLINK:
          return pericalLinkEpp ? { type, ...pericalLinkEpp, ...commonValues } : null;
        case ExternalPressureProfileType.FLUIDTOSHOE:
          return fluidToShoeEpp ? { type, ...fluidToShoeEpp, ...commonValues } : null;
        case ExternalPressureProfileType.MUDANDWETCEMENT:
          return mudAndWetCementEpp ? { type, ...mudAndWetCementEpp, ...commonValues } : null;
        case ExternalPressureProfileType.MUDANDCEMENTMIXWATER:
          return mudAndCementMixWaterEpp ? { type, ...mudAndCementMixWaterEpp, ...commonValues } : null;
        case ExternalPressureProfileType.INTERPOLATIONTOPOREPRSSURE:
          return interpolationToPorePressureEpp ? { type, ...interpolationToPorePressureEpp, ...commonValues } : null;
        case ExternalPressureProfileType.MUDANDPOREPRESSURE:
          return mudAndPorePressureEpp ? { type, ...mudAndPorePressureEpp, ...commonValues } : null;
        case ExternalPressureProfileType.POREPRESSURE:
          return porePressureEpp ? { type, ...porePressureEpp, ...commonValues } : null;
        case ExternalPressureProfileType.TRAPPEDCASINGPRESSURE:
          return trappedCasingPressureEpp ? { type, ...trappedCasingPressureEpp, ...commonValues } : null;
      }
    }
  });
  
  public defaultPressures: PressurePoint[] = [];
  public isLoading = signal<boolean>(false);
  public epps: SelectItem[] = [
    { label: 'Custom', value: 'CustomExternalPressures' },
    { label: 'PERICAL Link', value: 'ThermalLink' },
    // { label: 'Fluid to Shoe', value: 'MudToShoe' },
    { label: 'Mud and Wet Cement', value: 'MudAndWetCement' },
    { label: 'Mud and Cement Mix-Water', value: 'MudAndCementMixWater' },
    { label: 'Interpolation to Pore Pressure', value: 'InterpolationToPorePressure' },
    { label: 'Mud to TOC/PP in Cement', value: 'MudAndPorePressure' },
    { label: 'Pore Pressure', value: 'PorePressure' },
    // { label: 'Trapped Casing Pressure', value: 'TrappedCasingPressure' }
  ];

  public ExternalPressureProfileType: typeof ExternalPressureProfileType = ExternalPressureProfileType;

  ngOnInit(): void {
    if (!this.currentString['hasCement'] || this.currentString.topOfCementMd >= this.currentString.shoeMd) {
      this.epps = this.epps.filter(x => x.value !== "MudAndWetCement");
      this.epps = this.epps.filter(x => x.value !== "MudAndCementMixWater");
      this.epps = this.epps.filter(x => x.value !== "InterpolationToPorePressure");
      this.epps = this.epps.filter(x => x.value !== "MudAndPorePressure");
      this.epps = this.epps.filter(x => x.value !== "PorePressure");

      this.epps.push({ label: 'Mud/Brine to Shoe', value: 'MudAndWetCement' });
    }

    if (this.currentString.type == 'Tieback') {
      this.epps = this.epps.filter(x => x.value !== "InterpolationToPorePressure");
      this.epps = this.epps.filter(x => x.value !== "MudAndPorePressure");
      this.epps = this.epps.filter(x => x.value !== "PorePressure");
    }

    if (this.currentString.type == 'Tubing') {
      this.epps.push({ label: 'Annular Fluid', value: 'MudToShoe' });
      this.epps = this.epps.filter(x => x.value !== "TrappedCasingPressure");
    } else {
      this.epps.push({ label: 'Fluid to Shoe', value: 'MudToShoe' });
    }
  }

    public onEppTypeChange(event: any) {
      if (event.value === ExternalPressureProfileType.CUSTOMEPP) {
        this.isLoading.set(true);
        this._loadCaseService.getPressuresAndTemperatures(this.loadCaseIndex).subscribe(data => {
          this.defaultPressures = data.externalPressurePoints
            .filter((point, index, self) => 
              index === self.findIndex((p) => p.measuredDepth === point.measuredDepth)
            );
          this.isLoading.set(false);
        });
      }
    }
}
